/**
 * PendingInvitationsList — Displays pending buddy group invitations
 * with accept/decline actions.
 *
 * Renders at the top of the My Buddy Groups page. Returns null
 * when there are no pending invitations. Each invitation shows the
 * group name, who sent the invite, and the current member roster
 * with the primary cardholder marked.
 */

import { Mail, Check, X, Loader2, Crown, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PendingInvitation } from '@/services/buddyGroupInvitationService';

interface PendingInvitationsListProps {
  invitations: PendingInvitation[];
  onAccept: (invitationId: string) => void;
  onDecline: (invitationId: string) => void;
  isLoading?: boolean;
  /** ID of the invitation currently being accepted/declined */
  processingId?: string | null;
}

export function PendingInvitationsList({
  invitations,
  onAccept,
  onDecline,
  isLoading = false,
  processingId = null,
}: PendingInvitationsListProps) {
  // Don't render when empty and not loading
  if (!isLoading && invitations.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold">Pending Invitations</h2>
      </div>

      {isLoading && invitations.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading invitations...
        </div>
      )}

      <div className="space-y-2">
        {invitations.map((inv) => {
          const isProcessing = processingId === inv.invitationId;

          return (
            <div
              key={inv.invitationId}
              className="rounded-lg border bg-card p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {inv.groupName ?? 'Unnamed Group'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Invited by {inv.invitedByName ?? 'a member'}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => onAccept(inv.invitationId)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => onDecline(inv.invitationId)}
                    disabled={isProcessing}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>

              {/*
                Privacy disclosure — accepting the invitation is the
                trust gesture that opts the invitee into email
                visibility for the group's members. Surfaced before
                the user clicks Accept so consent is informed.
              */}
              <div
                className="mt-2 flex items-start gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground"
                role="note"
              >
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" aria-hidden />
                <span>
                  Accepting will share your email address with this
                  group's members. Your email stays private until you
                  accept.
                </span>
              </div>

              {inv.members.length > 0 && (
                <div className="mt-3 border-t pt-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                    Members ({inv.members.length})
                  </p>
                  <ul className="space-y-1">
                    {inv.members.map((m) => (
                      <li
                        key={m.userProfileId}
                        className="flex items-start gap-1.5 text-xs"
                      >
                        {m.isPrimary ? (
                          <Crown
                            className="h-3 w-3 mt-0.5 text-amber-500 shrink-0"
                            aria-label="Primary cardholder"
                          />
                        ) : (
                          <span
                            className="inline-block h-3 w-3 mt-0.5 shrink-0"
                            aria-hidden
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-1.5">
                            <span className="truncate">
                              {m.displayName ?? 'Unnamed member'}
                            </span>
                            {m.isPrimary && (
                              <span className="text-[10px] uppercase tracking-wide text-amber-600 font-medium">
                                Primary
                              </span>
                            )}
                          </div>
                          {m.email && (
                            <a
                              href={`mailto:${m.email}`}
                              className="block truncate text-muted-foreground hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {m.email}
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
